import moment from "moment";
import {
  PacingMonthData,
  PacingStatus,
  PacingTotals,
  ComparisonPeriod,
  RevenueSummaryMonth,
  AnnualBudgetMonth,
  PickUpReportItem,
  STATUS_THRESHOLDS,
} from "./types";

interface PickUpResponse {
  pickUpReportByDates?: PickUpReportItem[];
  totalBookings?: number;
  totalRevenue?: number;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function calculateStatus(percentToGoal: number | null): PacingStatus {
  if (percentToGoal === null) return PacingStatus.NoData;
  if (percentToGoal >= STATUS_THRESHOLDS.ON_TRACK) return PacingStatus.OnTrack;
  if (percentToGoal >= STATUS_THRESHOLDS.WATCH) return PacingStatus.Watch;
  return PacingStatus.NeedsAction;
}

export function getComparisonShiftDays(period: ComparisonPeriod): number {
  switch (period) {
    case "1_day":
      return 1;
    case "3_days":
      return 3;
    case "7_days":
      return 7;
    case "14_days":
      return 14;
    case "30_days":
      return 30;
    default:
      return 7;
  }
}

function safePercentage(
  value: number,
  total: number | null | undefined
): number | null {
  if (total === null || total === undefined || total === 0) return null;
  return (value / total) * 100;
}

function safeVariancePercent(current: number, prior: number): number | null {
  if (prior === 0) return current > 0 ? 100 : null;
  return ((current - prior) / prior) * 100;
}

function safeRate(
  revenue: number | null,
  rooms: number | null
): number | null {
  if (revenue === null || rooms === null || rooms === 0) return null;
  return revenue / rooms;
}

/**
 * Parses a date string from PickUp API response.
 * - Daily view (≤30 days): "2026-02-15" (YYYY-MM-DD)
 * - Monthly view (>30 days): "February 2026" (MMMM YYYY)
 * Returns the month number (1-12)
 */
function parseMonthFromDate(dateStr: string): number {
  // Daily view: ISO format (2026-02-15)
  const isoDate = moment(dateStr, "YYYY-MM-DD", true);
  if (isoDate.isValid()) {
    return isoDate.month() + 1;
  }

  // Monthly view: "Month Year" format (February 2026)
  const monthYearDate = moment(dateStr, "MMMM YYYY", true);
  if (monthYearDate.isValid()) {
    return monthYearDate.month() + 1;
  }

  return 0;
}

/**
 * Aggregates pickup data by month.
 * PickUp items have a `date` field (the stay date) and `sum` (revenue), `bookings` (rooms).
 * Date can be either "2026-02-15" (daily view) or "February 2026" (monthly view).
 */
function aggregatePickupByMonth(
  pickupDates: PickUpReportItem[] | undefined
): Map<number, { rooms: number; revenue: number }> {
  const result = new Map<number, { rooms: number; revenue: number }>();
  if (!pickupDates) return result;

  for (const item of pickupDates) {
    const month = parseMonthFromDate(item.date);
    if (month === 0) continue; // Skip invalid dates

    const existing = result.get(month) || { rooms: 0, revenue: 0 };
    result.set(month, {
      rooms: existing.rooms + (item.bookings || 0),
      revenue: existing.revenue + (item.sum || 0),
    });
  }
  return result;
}

export function transformToPacingData(
  summaryData: RevenueSummaryMonth[] | undefined,
  pickupData: PickUpResponse | undefined,
  fallbackBudget: AnnualBudgetMonth[],
  year: number
): PacingMonthData[] {
  if (!summaryData) return [];

  const currentMonth = moment().month() + 1;
  const currentYear = moment().year();

  // Aggregate pickup by month for prior calculation
  const pickupByMonth = aggregatePickupByMonth(pickupData?.pickUpReportByDates);

  return summaryData.map((monthData) => {
    const month = monthData.month;
    const isPastMonth =
      year < currentYear || (year === currentYear && month < currentMonth);
    const isCurrentMonth = year === currentYear && month === currentMonth;

    // Get pickup amounts for this month
    const monthPickup = pickupByMonth.get(month) || { rooms: 0, revenue: 0 };

    // Budget: prefer embedded data from RevenueSummary, fallback to onboarding
    const budgetFromResponse = monthData.budget;
    const budgetFallback = fallbackBudget.find((b) => b.month === month);
    const budgetRevenue =
      budgetFromResponse?.roomsRevenue ?? budgetFallback?.roomsRevenue ?? null;
    const budgetRooms =
      budgetFromResponse?.soldRooms ?? budgetFallback?.soldRooms ?? null;
    const budgetOccupancy =
      budgetFromResponse?.occupancy ?? budgetFallback?.occupancy ?? null;

    // For past months: use actual data only
    // For current month: combine actual (past days) + OTB (remaining days)
    // For future months: use OTB data only
    let currentRevenue: number;
    let currentOccupancy: number;
    let currentRooms: number;
    let currentRate: number | null;

    if (isCurrentMonth) {
      // Current month: combine actual + OTB
      currentRevenue =
        (monthData.actualRevenue || 0) + (monthData.onBooksRevenue || 0);
      currentRooms =
        (monthData.actualBookings || 0) + (monthData.onBooksBookings || 0);
      // Use higher occupancy (actual for past days likely higher than OTB projection)
      currentOccupancy = Math.max(
        monthData.actualOccupancy || 0,
        monthData.onBooksOccupancy || 0
      );
      // Calculate ADR from combined totals
      currentRate = currentRooms > 0 ? currentRevenue / currentRooms : null;
    } else if (isPastMonth) {
      // Past months: actual data only
      currentRevenue = monthData.actualRevenue || 0;
      currentRooms = monthData.actualBookings || 0;
      currentOccupancy = monthData.actualOccupancy || 0;
      currentRate = monthData.actualAdr || null;
    } else {
      // Future months: OTB data only
      currentRevenue = monthData.onBooksRevenue || 0;
      currentRooms = monthData.onBooksBookings || 0;
      currentOccupancy = monthData.onBooksOccupancy || 0;
      currentRate = monthData.onBooksAdr || null;
    }

    // Prior = Current - Pickup (what was OTB before the pickup window)
    const priorRooms = Math.max(0, currentRooms - monthPickup.rooms);
    const priorRevenue = Math.max(0, currentRevenue - monthPickup.revenue);

    // Calculate prior rate from prior revenue/rooms
    const priorRate = safeRate(priorRevenue, priorRooms);

    // Approximate prior occupancy (proportional reduction)
    const priorOccupancy =
      currentRooms > 0
        ? currentOccupancy * (priorRooms / currentRooms)
        : currentOccupancy;

    const stlyRooms = monthData.bookingsPrevYear || 0;
    const stlyRevenue = monthData.revenuePrevYear || 0;
    const stlyOccupancy = monthData.occupancyPrevYear || 0;
    const stlyRate = stlyRooms > 0 ? stlyRevenue / stlyRooms : null;

    // Budget rate from embedded data or fallback
    const budgetRate = budgetFromResponse?.adr ?? budgetFallback?.adr ?? null;

    // Variance calculations (Current - Prior = Pickup)
    const varianceRooms = currentRooms - priorRooms;
    const varianceRevenue = currentRevenue - priorRevenue;
    const varianceRate =
      currentRate !== null && priorRate !== null
        ? currentRate - priorRate
        : null;

    // Reach to Goal calculations
    const reachToGoalRooms =
      budgetRooms !== null ? budgetRooms - currentRooms : null;
    const reachToGoalRevenue =
      budgetRevenue !== null ? budgetRevenue - currentRevenue : null;
    const reachToGoalRate =
      budgetRate !== null && currentRate !== null
        ? budgetRate - currentRate
        : null;

    const percentToGoalRevenue = safePercentage(currentRevenue, budgetRevenue);
    const percentToGoalRooms = safePercentage(currentRooms, budgetRooms);

    const varianceVsSTLY = currentRevenue - stlyRevenue;
    const varianceVsSTLYPercent = safeVariancePercent(
      currentRevenue,
      stlyRevenue
    );

    return {
      month,
      year,
      monthName: MONTH_NAMES[month - 1],
      isPastMonth: isPastMonth || isCurrentMonth,

      budgetRooms,
      budgetRevenue,
      budgetOccupancy,

      currentRooms,
      currentRevenue,
      currentOccupancy,

      priorRooms,
      priorRevenue,
      priorOccupancy,

      stlyRooms,
      stlyRevenue,
      stlyOccupancy,
      stlyRate,

      budgetRate,
      currentRate,
      priorRate,

      percentToGoalRooms,
      percentToGoalRevenue,

      varianceRooms,
      varianceRevenue,
      varianceRate,

      varianceVsSTLY,
      varianceVsSTLYPercent,

      reachToGoalRooms,
      reachToGoalRevenue,
      reachToGoalRate,

      roomsStatus: calculateStatus(percentToGoalRooms),
      revenueStatus: calculateStatus(percentToGoalRevenue),
    };
  });
}

export function calculatePacingTotals(data: PacingMonthData[]): PacingTotals | null {
  if (!data || data.length === 0) return null;

  const budgetRooms = data.reduce((sum, m) => sum + (m.budgetRooms || 0), 0);
  const budgetRevenue = data.reduce((sum, m) => sum + (m.budgetRevenue || 0), 0);
  const currentRooms = data.reduce((sum, m) => sum + m.currentRooms, 0);
  const currentRevenue = data.reduce((sum, m) => sum + m.currentRevenue, 0);
  const priorRooms = data.reduce((sum, m) => sum + m.priorRooms, 0);
  const priorRevenue = data.reduce((sum, m) => sum + m.priorRevenue, 0);
  const stlyRooms = data.reduce((sum, m) => sum + m.stlyRooms, 0);
  const stlyRevenue = data.reduce((sum, m) => sum + m.stlyRevenue, 0);

  const budgetRate = budgetRooms > 0 ? budgetRevenue / budgetRooms : null;
  const currentRate = currentRooms > 0 ? currentRevenue / currentRooms : null;
  const priorRate = priorRooms > 0 ? priorRevenue / priorRooms : null;
  const stlyRate = stlyRooms > 0 ? stlyRevenue / stlyRooms : null;

  const varianceRooms = currentRooms - priorRooms;
  const varianceRevenue = currentRevenue - priorRevenue;
  const varianceRate =
    currentRate !== null && priorRate !== null ? currentRate - priorRate : null;

  const reachToGoalRooms = budgetRooms > 0 ? budgetRooms - currentRooms : null;
  const reachToGoalRevenue = budgetRevenue > 0 ? budgetRevenue - currentRevenue : null;
  const reachToGoalRate =
    budgetRate !== null && currentRate !== null ? budgetRate - currentRate : null;

  const percentToGoal = budgetRevenue > 0 ? (currentRevenue / budgetRevenue) * 100 : null;

  return {
    budgetRooms,
    budgetRevenue,
    budgetRate,
    currentRooms,
    currentRevenue,
    currentRate,
    priorRooms,
    priorRevenue,
    priorRate,
    varianceRooms,
    varianceRevenue,
    varianceRate,
    reachToGoalRooms,
    reachToGoalRevenue,
    reachToGoalRate,
    percentToGoal,
    stlyRooms,
    stlyRevenue,
    stlyRate,
  };
}
